import { SellerAccountStatus } from "@/Apollo/schema";
import { IconNavigationChevronDown } from "@/assets/icons/figma/IconNavigationChevronDown";
import { cn } from "@/common/utils/css.utils";
import { STATUS_QUESTIONS } from "@/components/Account/payment/payment.static";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";
import { Disclosure } from "@headlessui/react";

type StatusQuestionsProps = {
  status: SellerAccountStatus;
};

/**
 * Three collapsed questions per status, the ones Maison gets asked. The same
 * Headless UI Disclosure as the site's other FAQ accordions
 * (ProBuyerFaqAccordion, ContactFaq); `unmount={false}` keeps the answers in
 * the HTML whether or not a row is open.
 */
export const StatusQuestions = ({ status }: StatusQuestionsProps) => {
  const { copy } = usePaymentCopy();
  const questions = STATUS_QUESTIONS[status];

  return (
    <section
      aria-labelledby="payment-questions-title"
      className="flex flex-col gap-2"
    >
      <Heading as="h2" size="h5" id="payment-questions-title">
        {copy("sectionQuestions")}
      </Heading>
      <div className="flex flex-col divide-y divide-grey-200 border-b border-grey-200">
        {questions.map(item => (
          <Disclosure key={item.question} as="div" className="py-3">
            {({ open }) => (
              <>
                <Disclosure.Button className="flex w-full items-center justify-between gap-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  <Body as="span" className="font-bold">
                    {copy(item.question)}
                  </Body>
                  <IconNavigationChevronDown
                    className={cn(
                      "size-6 shrink-0 text-brand-500 transition-transform",
                      open && "rotate-180"
                    )}
                  />
                </Disclosure.Button>
                <Disclosure.Panel unmount={false} className="pt-2">
                  <Body as="p">{copy(item.answer)}</Body>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ))}
      </div>
    </section>
  );
};
